import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";

import { useAuth } from "../context/AuthContext";

export enum NavigationContext {
  ReactGeneral = "react-general",
  ReactDemoTour = "react-demo-tour",
  DjangoTemplate = "django-template",
}

interface NavigationProps {
  context?: NavigationContext;
}

const DJANGO_LINK_PATHS = [
  '/accounts/3rdparty/',
]

export const Navigation: React.FC<NavigationProps> = ({ context = NavigationContext.ReactGeneral }) => {
  const { user, logout, updateUser } = useAuth();
  const location = useLocation();
  const [isUpdatingDailyMessage, setIsUpdatingDailyMessage] = useState(false);

  const handleLogout = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await logout();
      window.location.href = '/';
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const handleDailyMessageToggle = async (e: React.ChangeEvent<HTMLInputElement>) => {
    setIsUpdatingDailyMessage(true);
    try {
      const result = await updateUser({
        is_daily_message_paused: e.target.checked
      });

      if (!result.success) {
        console.error('Failed to update daily message setting:', result.error);
        // Reset the checkbox on error
        e.target.checked = !e.target.checked;
      }
    } catch (error) {
      console.error('Error updating daily message setting:', error);
      // Reset the checkbox on error
      e.target.checked = !e.target.checked;
    } finally {
      setIsUpdatingDailyMessage(false);
    }
  };

  // Helper to determine if a path is active
  const isActive = (path: string): boolean => {
    return location.pathname === path;
  };

  // Helper to create the right type of link based on context
  const NavigationLink: React.FC<{
    to: string;
    className: string;
    children: React.ReactNode;
  }> = ({ to, className, children }) => {
    // Always use regular anchor tags for external links or in Django context
    if (context === NavigationContext.DjangoTemplate || DJANGO_LINK_PATHS.includes(to)) {
      return (
        <a href={to} className={className}>
          {children}
        </a>
      );
    }

    // Use React Router Link in React context
    return (
      <Link to={to} className={className}>
        {children}
      </Link>
    );
  };

  const isInDemoTour = () => {
    return context === NavigationContext.ReactDemoTour;
  }

  const showChatLink = () => {
    return user && !isInDemoTour();
  }

  const showOnboardedUserLinks = () => {
    return user && !isInDemoTour();
  }

  const showDemoTourLink = () => {
    return isInDemoTour() || location.pathname === '/';
  }

  const tourLink = () => {
    let linkData = {
      '/guest-tour': { path: '/guest-tour', text: 'Guest Tour' },
      '/demo-tour': { path: '/demo-tour', text: 'Demo Tour' },
    }[location.pathname]
    if (!linkData) {
      if (!user) {
        linkData = { path: '/guest-tour', text: 'Take the Tour' };
      } else {
        return null
      }
    }
    return (
      <NavigationLink
        to={linkData.path}
        className={`nav-link ${isActive(linkData.path) ? 'nav-link--active' : ''}`}
      >
        {linkData.text}
      </NavigationLink>
    )
  }

  return (
    <nav className="main-nav">
      <div className={`nav-content ${isInDemoTour() ? 'demo-tour-nav' : ''}`}>
        <div className="nav-left">
          <NavigationLink
            to="/"
            className={`nav-link ${isActive('/') ? 'nav-link--active' : ''}`}
          >
            Home
          </NavigationLink>
          {showChatLink() && (
            <NavigationLink
              to="/chat"
              className={`nav-link ${isActive('/chat') ? 'nav-link--active' : ''}`}
            >
              Chat
            </NavigationLink>
          )}
          {showDemoTourLink() && (
            tourLink()
          )}
        </div>

        <div className="nav-right">
          {user ? (
            <div className="avatar-container">
              <div className="avatar">{user.initials}</div>
              <div className="dropdown-menu">
                <div className="user-email">{user.email}</div>
                {showOnboardedUserLinks() && (
                  <>
                    <NavigationLink
                      to="/memories"
                      className={`dropdown-item ${isActive('/memories') ? 'dropdown-item--active' : ''}`}
                    >
                      Memories
                    </NavigationLink>
                    <NavigationLink
                      to="/resources"
                      className={`dropdown-item ${isActive('/resources') ? 'dropdown-item--active' : ''}`}
                    >
                      History
                    </NavigationLink>
                    {/* Account Connections is always treated as external */}
                    <NavigationLink
                      to="/accounts/3rdparty/"
                      className={`dropdown-item ${location.pathname.includes('3rdparty') ? 'dropdown-item--active' : ''}`}
                    >
                      Calendar Connections
                    </NavigationLink>

                    {/* Daily Message Toggle - only show when calendar connections are visible */}
                    <div className="dropdown-item">
                      <label
                        className={`toggle-slider-label ${isUpdatingDailyMessage ? 'toggle-slider-label--disabled' : ''}`}
                        style={{ marginBottom: 0 }}
                      >
                        <span className="toggle-slider-label__text">Pause daily messages</span>
                        <div className="toggle-slider">
                          <input
                            type="checkbox"
                            className="toggle-slider__input"
                            checked={user?.is_daily_message_paused || false}
                            onChange={handleDailyMessageToggle}
                            disabled={isUpdatingDailyMessage}
                          />
                          <div className="toggle-slider__track">
                            <div className="toggle-slider__thumb"></div>
                          </div>
                        </div>
                      </label>
                    </div>
                  </>
                )}
                <form onSubmit={handleLogout}>
                  <button type="submit" className="dropdown-button">
                    Sign Out
                  </button>
                </form>
              </div>
            </div>
          ) : (
            location.pathname === "/auth/login" ? (
              <NavigationLink to="/auth/signup" className="nav-link">Sign Up</NavigationLink>
            ) : (
              <NavigationLink to="/auth/login" className="nav-link">Sign In</NavigationLink>
            )
          )}
        </div>
      </div>
    </nav>
  );
};
